<template>
    <div class="numbers-widget">
        <div class="widget-header">
            <div class="left">
                <i
                    class="material-icons drag-handle"
                    v-tippy="{
                        placement: 'top',
                        theme: 'light',
                        arrow: true,
                        animation: 'scale-extreme',
                        duration: [200, 200],
                        hideOnClick: true,
                    }"
                    content="Sürükleyerek taşıyabilirsiniz"
                    >drag_indicator</i
                >
                <widget-title :widget="widget" :table="table" @widget-updated="handleWidgetUpdated" />
            </div>

            <widget-actions
                @fullscreen="handleFullscreen"
                @edit="handleEdit"
                @rename="handleRename"
                @duplicate="handleDuplicate"
                @dock="handleDock"
                @export="handleExport"
                @delete="$emit('delete', widget._id)"
                :title="widget.title"
                :widget="widget"
                :table="table"
                @widget-updated="handleWidgetUpdated"
                @widget-duplicated="handleWidgetDuplicated"
                @widget-deleted="handleWidgetDeleted"
            />
        </div>
        <div class="widget-content">
            <empty-numbers
                v-if="!hasValidSettings || !numberData || numberData.error"
                :error="numberData ? numberData.error : null"
                :has-valid-settings="hasValidSettings"
                @edit="handleEdit"
            />
            <div v-else-if="loading" class="loading-overlay">
                <loading-wave text="Veriler yükleniyor..." />
            </div>
            <div v-else class="number-display">
                <div class="value-container">
                    <span v-if="$_.get(widget, 'settings.customization.unit.position') === 'left'" class="unit">
                        {{ unitValue }}
                    </span>
                    <span class="value" :style="{ color: widget.settings.customization.color }">{{ formattedValue }}</span>
                    <span v-if="$_.get(widget, 'settings.customization.unit.position') === 'right'" class="unit">
                        {{ unitValue }}
                    </span>
                </div>
            </div>
        </div>

        <numbers-settings-modal
            v-if="showSettingsModal"
            :visible="showSettingsModal"
            :widget="widget"
            :fields="table.fields"
            @close="showSettingsModal = false"
            @save="handleSettingsSave"
            :table="table"
        />
    </div>
</template>

<script>
import Vue from "vue";
import { TABLE_API } from "../../../../../../api/table";
import log2 from "../../../../../../utils/log2";
import WidgetActions from "../charts/components/WidgetActions.vue";
import WidgetTitle from "../charts/components/WidgetTitle.vue";
import EmptyNumbers from "./components/EmptyNumbers.vue";
import NumbersSettingsModal from "./components/NumbersSettingsModal.vue";
import { debounce } from "lodash";
import LoadingWave from "../../../../../../components/common/LoadingWave.vue";

const log = log2("NumbersWidget");

export default Vue.extend({
    name: "NumbersWidget",
    components: {
        WidgetActions,
        WidgetTitle,
        EmptyNumbers,
        NumbersSettingsModal,
        LoadingWave,
    },
    props: {
        widget: {
            type: Object,
            required: true,
        },
        
        table: {
            type: Object,
            required: true,
        },
        upstreamFilters: {
            type: Object,
            default: () => ({filters: [], searchQuery: ""}),
        },
    },
    data() {
        return {
            numberData: null,
            showSettingsModal: false,
            loading: false,
        };
    },
    computed: {
        hasValidSettings() {
            return (
                !!this.widget.settings &&
                ((this.widget.settings.calculationType === "count") ||
                (this.widget.settings.fields && this.widget.settings.fields.length > 0))
            );
        },
        formattedValue() {
            if (!this.numberData) return "0";
            const value = this.numberData.value;
            const decimalFormat = this.widget.settings.decimalFormat || 0;
            return value.toLocaleString('tr-TR', {
                minimumFractionDigits: decimalFormat,
                maximumFractionDigits: decimalFormat
            });
        },
        unitValue() {
            return this.$_.get(this.widget, 'settings.customization.unit.value');
        }
    },
    async mounted() {
        await this.fetchNumberData();
    },
    created() {
        this.debouncedFetchNumberData = debounce(async () => {
            try {
                this.loading = true;
                this.numberData = await TABLE_API.getNumberData(this.table._id, this.widget._id, this.upstreamFilters);
            } catch (error) {
                console.error("Failed to fetch number data", error);
                this.$message.error("Sayı verisi yüklenemedi");
            } finally {
                this.loading = false;
            }
        }, 500);
    },
    methods: {
        async fetchNumberData() {
            await this.debouncedFetchNumberData();
        },
        handleFullscreen() {
            // TODO: Tam ekran modunu implement et
        },
        async handleRename(newName) {
            try {
                await TABLE_API.updateWidget(this.table._id, this.widget.page, this.widget._id, {
                    title: newName,
                });
                this.$emit("widget-updated", {
                    ...this.widget,
                    title: newName,
                });
            } catch (error) {
                console.error("Widget adı güncellenirken hata:", error);
                throw error;
            }
        },
        async handleDuplicate() {
            try {
                const result = await TABLE_API.duplicateWidget(this.table._id, this.widget.page, this.widget._id);
                this.$message.success("Widget başarıyla çoğaltıldı");
                this.$emit("widget-duplicated", {
                    widget: result.widget,
                    page: result.page,
                });
            } catch (error) {
                console.error("Widget çoğaltılırken hata:", error);
                this.$message.error("Widget çoğaltılırken bir hata oluştu");
            }
        },
        handleDock() {
            // TODO: Sabitleme işlemini implement et
        },
        handleExport() {
            // TODO: Dışa aktarma işlemini implement et
        },
        handleEdit() {
            this.showSettingsModal = true;
        },
        async handleSettingsSave(newSettings) {
            try {
                await TABLE_API.updateWidget(this.table._id, this.widget.page, this.widget._id, {
                    settings: newSettings,
                });
                this.$message.success("Widget ayarları güncellendi");
                this.$emit("widget-updated", {
                    ...this.widget,
                    settings: newSettings,
                });
                this.showSettingsModal = false;
                await this.fetchNumberData();
            } catch (error) {
                console.error("Widget ayarları güncellenirken hata:", error);
                this.$message.error("Widget ayarları güncellenirken bir hata oluştu");
            }
        },
        async handleWidgetUpdated(updatedWidget) {
            this.$emit("widget-updated", updatedWidget);
        },
        async handleWidgetDuplicated(result) {
            this.$emit("widget-duplicated", result);
        },
        async handleWidgetDeleted(widgetId) {
            this.$emit("delete", widgetId);
        },
    },
    watch: {
        "widget.settings": {
            handler() {
                this.fetchNumberData();
            },
            deep: true,
        },
        upstreamFilters: {
            handler() {
                this.fetchNumberData();
            },
            deep: true,
        },
    },
    beforeDestroy() {
        if (this.debouncedFetchNumberData) {
            this.debouncedFetchNumberData.cancel();
        }
    },
});
</script>

<style scoped lang="scss">
.numbers-widget {
    height: 100%;
    display: flex;
    flex-direction: column;
    background: #fff;
    border-radius: 4px;
    overflow: hidden;
    position: relative;
    border: 2px solid #fafafa;
    transition: border-color 0.2s ease;

    &:hover {
        border-color: #1890ff;

        .drag-handle {
            visibility: visible;
            line-height: 1px;
        }
    }

    .loading-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(255, 255, 255, 0.8);
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        z-index: 2;
    }

    .drag-handle {
        transform: translateY(-50%);
        font-size: 20px;
        color: rgba(0, 0, 0, 0.25);
        cursor: move;
        transition: opacity 0.2s, color 0.2s, left 0.2s;
        visibility: hidden;
        line-height: 1px;
        &:hover {
            color: rgba(0, 0, 0, 0.45);
            left: -8px;
        }
    }

    .widget-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 16px;
        border-bottom: 1px solid #e8e8e8;
        background: #fafafa;
        .left {
            display: flex;
            align-items: center;
            gap: 5px;
            width: -webkit-fill-available;
        }
    }

    .widget-content {
        flex: 1;
        min-height: 0;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;

        .number-display {
            text-align: center;
            padding: 20px;

            .value-container {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 4px;

                .value {
                    font-size: 36px;
                    font-weight: bold;
                    color: #1890ff;
                }

                .unit {
                    font-size: 18px;
                    color: #666;
                }
            }
        }
    }
}
</style> 